import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getDataContest } from '../redux/data/selectors'
import { useWeather, getWeatherAt, getWeatherChanges, getWeatherIcons, getContestDay, CONDITIONS, WEATHER_CREDIT } from '../tools/Weather'
import { seconds2clock } from '../tools/Time'
import Icon from './Icon'

/**
 * Width in pixels of a weather label, as in Charts.less
 * @type {number}
 */
const LABEL_WIDTH = 56

/**
 * Narrowest space in pixels between the middles of two weather labels; closer labels are left out
 * @type {number}
 */
const MIN_LABEL_SPACE = LABEL_WIDTH + 4

/**
 * Height in pixels to keep free above the plot: the weather labels and some room below them
 * @type {number}
 */
export const WEATHER_HEIGHT = 30

/**
 * Credit the source of the weather with its licence in brackets, required next to the data; with
 * the icon closest to the Open-Meteo logo, a sun behind a cloud
 * @returns {JSX.Element} links to the source and the licence
 */
export const WeatherCredit = () =>
(
  <span className="weather-credit">
    <Icon icon="weather-partly"/>
    <span>
      <a href={ WEATHER_CREDIT.url } target="_blank" rel="noreferrer">{ WEATHER_CREDIT.label }</a>
      { ' (' }
      <a href={ WEATHER_CREDIT.licenceUrl } target="_blank" rel="noreferrer">{ WEATHER_CREDIT.licence }</a>
      { ')' }
    </span>
  </span>
)

/**
 * Place times of day on an axis that isn't by time, such as lap numbers: between the two laps around it
 * @param {Array<number>} times time of each point in seconds since midnight, in time order
 * @param {Array<number>} xs value of each point on the axis
 * @returns {Function} converts seconds since midnight to a value on the axis, null outside the points
 */
export const getTimeToAxis = ( times, xs ) => ( seconds ) =>
{
  const after = times.findIndex( time => time >= seconds )
  if ( after < 0 ) return null
  if ( after === 0 ) return seconds === times[ 0 ] ? xs[ 0 ] : null

  const before = after - 1
  const part = ( seconds - times[ before ] ) / Math.max( 1, times[ after ] - times[ before ] )
  return xs[ before ] + part * ( xs[ after ] - xs[ before ] )
}

/**
 * Say what changed at a label: rain, snow or hail starting or stopping, other weather, strong wind
 * @param {Object} label label with its condition and windy, and for a change the condition and windy before
 * @returns {string} what changed, or the weather at a label that isn't a change
 */
const getChange = ( label ) =>
{
  const condition = CONDITIONS[ label.condition ]
  if ( !label.change ) return condition.label

  const parts = []
  if ( label.condition !== label.before )
  {
    const before = CONDITIONS[ label.before ]
    parts.push( condition.wet
      ? `${ condition.label } starts`
      : before && before.wet ? `${ before.label } stops, ${ condition.label.toLowerCase() }` : condition.label )
  }
  if ( label.windy !== label.beforeWindy )
  {
    parts.push( label.windy ? 'strong wind starts' : 'wind drops' )
  }

  const change = parts.join( ', ' )
  return change.charAt( 0 ).toUpperCase() + change.slice( 1 )
}

/**
 * Describe the weather at a label, for its tooltip: what changed, the temperature, any rain and the wind
 * @param {Object} label label with seconds, the weather there, its condition and windy, and for a change what was before
 * @returns {string} description
 */
const describe = ( label ) =>
{
  const { temperature, precipitation, wind } = label.weather
  const details = [
    getChange( label ),
    `${ temperature.toFixed( 1 ) } °C`,
    precipitation > 0 ? `${ precipitation.toFixed( 1 ) } mm in 15 minutes` : '',
    `wind ${ wind.beaufort } Bft from ${ wind.from }, gusts ${ wind.gusts } km/h`
  ]
  return `${ seconds2clock( label.seconds ) } ${ details.filter( Boolean ).join( ', ' ) }`
}

/**
 * Show the weather above a chart: the condition at the start and every change, such as rain
 * starting or stopping or strong wind, with a line through the plot; the temperature and weather when it
 * changes a degree
 * @param {Object} props component props
 * @param {Function} props.toX converts seconds since midnight to a value on the horizontal axis, null when it isn't on it
 * @param {number} props.from time of the first lap in seconds since midnight
 * @param {number} props.to time of the last lap in seconds since midnight
 * @param {Object} props.view shown part of the axis, with min and max
 * @param {number} props.left width of the chart area left of the plot
 * @param {number} props.right width of the chart area right of the plot
 * @param {number} props.top top of the plot, the labels go just above it
 * @param {number} props.bottom height of the chart area below the plot, where the change lines end
 * @returns {JSX.Element} weather labels and change lines
 */
const WeatherStrip = ( { toX = s => s, from = 0, to = 0, view = { min: 0, max: 0 }, left = 0, right = 0, top = WEATHER_HEIGHT, bottom = 0 } ) =>
{
  const weather = useWeather( getContestDay( useSelector( getDataContest ) ) )
  const ref = useRef( null )
  const [ width, setWidth ] = useState( 0 )

  // keep the plot width to place the labels and leave out those that would overlap
  useEffect( () =>
  {
    const measure = () => ref.current && setWidth( ref.current.clientWidth - left - right )
    measure()
    window.addEventListener( 'resize', measure )
    return () => window.removeEventListener( 'resize', measure )
  }, [ left, right ] )

  const range = view.max - view.min
  const ready = weather && range > 0 && width > 0

  /**
   * Get where a moment is on the plot, also outside the shown part
   * @param {number} seconds seconds since midnight
   * @returns {number|null} offset in pixels from the left of the plot, null when not on the axis
   */
  const place = ( seconds ) =>
  {
    const x = toX( seconds )
    return x === null ? null : ( x - view.min ) / range * width
  }

  // the weather at the first lap and every change after it, also outside the shown part
  const changes = !ready ? [] : getWeatherChanges( weather, from, to )
    .map( ( change, index, all ) => ( {
      ...change,
      before: index > 0 ? all[ index - 1 ].condition : '',
      beforeWindy: index > 0 && all[ index - 1 ].windy,
      offset: place( change.seconds ),
      weather: getWeatherAt( weather, change.seconds )
    } ) )
    .filter( change => change.offset !== null && change.weather )

  // and wherever the temperature reaches another whole degree
  const temperatures = []
  let degrees = changes.length ? Math.round( changes[ 0 ].weather.temperature ) : null
  ready && weather.ends.forEach( ( end, index ) =>
  {
    const rounded = Math.round( weather.temperature[ index ] )
    if ( end <= from || end > to || rounded === degrees ) return

    const offset = place( end )
    const at = getWeatherAt( weather, end )
    if ( offset === null || !at ) return

    degrees = rounded
    temperatures.push( { seconds: end, offset, weather: at, condition: at.condition, windy: at.windy } )
  } )

  // changes of the weather first, then the temperature where there is room
  const labels = []
  const fits = ( label ) => labels.every( l => Math.abs( l.offset - label.offset ) >= MIN_LABEL_SPACE )
  changes.forEach( change => fits( change ) && labels.push( change ) )
  temperatures.forEach( temperature => fits( temperature ) && labels.push( temperature ) )
  labels.sort( ( a, b ) => a.offset - b.offset )

  // labels move with the plot; the last one that would leave on the left sticks to the left edge
  // until the next one pushes it out, and the same on the right
  const half = LABEL_WIDTH / 2
  const first = labels.reduce( ( found, label, index ) => label.offset < half ? index : found, -1 )
  const last = labels.findIndex( label => label.offset > width - half )
  const shown = labels
    .map( ( label, index ) =>
    {
      if ( index < first || ( last >= 0 && index > last ) ) return null
      if ( index === first )
      {
        const next = labels[ index + 1 ]
        return { ...label, centre: next ? Math.min( half, next.offset - LABEL_WIDTH ) : half }
      }
      if ( index === last )
      {
        const before = labels[ index - 1 ]
        return { ...label, centre: before ? Math.max( width - half, before.offset + LABEL_WIDTH ) : width - half }
      }
      return { ...label, centre: label.offset }
    } )
    .filter( label => label && label.centre > -half && label.centre < width + half )

  /**
   * Fade a label out as it is pushed past an edge of the plot
   * @param {number} centre middle of the label in pixels from the left of the plot
   * @returns {number} opacity from 0, gone, to 1, fully in view
   */
  const getOpacity = ( centre ) => Math.max( 0, Math.min( 1, ( centre + half ) / LABEL_WIDTH, ( width + half - centre ) / LABEL_WIDTH ) )

  /**
   * Check whether a change makes the track wet or dry
   * @param {Object} change change with its condition and the condition before
   * @returns {boolean} whether it starts or stops raining, snowing or hailing
   */
  const isWetChange = ( change ) => CONDITIONS[ change.condition ].wet !== !!( CONDITIONS[ change.before ] || {} ).wet

  return (
    <div className="weather-strip" ref={ ref } style={ { top: top - WEATHER_HEIGHT, bottom } }>
      { changes.filter( change => change.change && change.offset >= 0 && change.offset <= width ).map( change => (
        <span
          key={ `line-${ change.seconds }` }
          className={ `weather-line ${ isWetChange( change ) ? 'wet' : '' }` }
          style={ { left: left + change.offset, top: WEATHER_HEIGHT } }
        />
      ) ) }
      { shown.map( label => (
        <span
          key={ `label-${ label.seconds }-${ label.condition }` }
          className={ `weather ${ CONDITIONS[ label.condition ].wet ? 'wet' : '' }` }
          style={ { left: left + label.centre, opacity: getOpacity( label.centre ) } }
          title={ describe( label ) }
        >
          { getWeatherIcons( label.condition, label.windy ).map( icon => <Icon key={ icon } icon={ icon }/> ) }
          { Math.round( label.weather.temperature ) }°
        </span>
      ) ) }
    </div>
  )
}

export default WeatherStrip
